import { createFileRoute, getRouteApi, Link, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { ArrowLeft, CalendarDays, Camera, CheckCircle2, Clock3, MapPin, MessageCircle, Phone, Play, Receipt, UserRound } from "lucide-react";
import { toast } from "sonner";
import { assignPartnerJobToTeamMember, getPartnerJobDetail, getPartnerJobWorkspaceState, unassignPartnerJobFromTeam, updatePartnerJobWorkspaceState } from "@/lib/partner-jobs.functions";
import { getPartnerTeam } from "@/lib/partner-business.functions";
import { getPartnerPaymentSnapshot } from "@/lib/marketplace-live.functions";
import { updateJobProgress } from "@/lib/partner.functions";
import { uploadPhoto } from "@/lib/uploads";
import { JobLocationMap } from "@/components/booking/JobLocationMap";
import { ScheduleSection } from "@/components/booking/ScheduleSection";
import { StatusPill } from "@/components/StatusPill";
import { JobJourney } from "@/components/jobs/JobJourney";
import { PartnerPaymentStatusCard } from "@/components/partner/PartnerPaymentStatusCard";
import { useBookingRealtime } from "@/hooks/useBookingRealtime";
import { bookingStatusLabel, gbp, ukDate, ukDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const authLayout = getRouteApi("/_authenticated");

export const Route = createFileRoute("/_authenticated/partner/jobs/$bookingId")({
  head: () => ({
    meta: [
      { title: "Assigned job — Shields" },
      { name: "description", content: "Your assigned job with full customer details." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PartnerJobPage,
});

function PartnerJobPage() {
  const { bookingId } = useParams({ from: "/_authenticated/partner/jobs/$bookingId" });
  const { user } = authLayout.useRouteContext();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["partner-job", bookingId],
    queryFn: () => getPartnerJobDetail({ data: { bookingId } }),
    refetchInterval: 60_000,
  });
  const { data: paymentState } = useQuery({
    queryKey: ["partner-payment", bookingId],
    queryFn: () => getPartnerPaymentSnapshot({ data: { bookingId } }),
    refetchInterval: 60_000,
  });
  const { data: team } = useQuery({
    queryKey: ["partner-team"],
    queryFn: () => getPartnerTeam(),
  });
  const { data: workState } = useQuery({
    queryKey: ["partner-job-workspace", bookingId],
    queryFn: () => getPartnerJobWorkspaceState({ data: { bookingId } }),
  });
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [checklist, setChecklist] = useState({ arrived_checked: false, scope_completed: false, left_safe_tidy: false });
  const [beforeFiles, setBeforeFiles] = useState<File[]>([]);
  const [afterFiles, setAfterFiles] = useState<File[]>([]);
  const [assigningMemberId, setAssigningMemberId] = useState<string | null>(null);
  const [unassigning, setUnassigning] = useState(false);

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["partner-job", bookingId] }),
      queryClient.invalidateQueries({ queryKey: ["partner-payment", bookingId] }),
      queryClient.invalidateQueries({ queryKey: ["lifecycle", bookingId] }),
      queryClient.invalidateQueries({ queryKey: ["partner-jobs-board"] }),
      queryClient.invalidateQueries({ queryKey: ["partner-job-workspace", bookingId] }),
      queryClient.invalidateQueries({ queryKey: ["partner-team"] }),
    ]);
  }

  useBookingRealtime(bookingId, () => {
    void refresh();
  });

  useEffect(() => {
    if (!workState?.ok) return;
    if (!notesDirty) setNotes(workState.notes);
    setChecklist(workState.checklist);
  }, [workState, notesDirty]);

  const workStateError = workState && !workState.ok ? workState.error : null;

  async function progress(action: "on_the_way" | "arrive" | "start" | "complete", e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    try {
      let evidence: Array<{ path: string; kind: "before" | "after" }> | undefined;
      if (action === "complete") {
        evidence = [];
        for (const file of beforeFiles) {
          evidence.push({ path: await uploadPhoto("job-evidence", file), kind: "before" });
        }
        for (const file of afterFiles) {
          evidence.push({ path: await uploadPhoto("job-evidence", file), kind: "after" });
        }
      }
      const result = await updateJobProgress({
        data: {
          bookingId,
          action,
          completionNotes: action === "complete" ? notes : undefined,
          evidence,
        },
      });
      if (!result.ok) {
        toast.error(result.error ?? "Could not update the job");
        return;
      }
      toast.success(action === "on_the_way" ? "Customer notified that you are on the way" : action === "arrive" ? "Arrival confirmed" : action === "start" ? "Job marked as started" : "Completion sent to the customer for approval");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }


  async function saveWorkState(nextChecklist = checklist, nextNotes = notes) {
    const result = await updatePartnerJobWorkspaceState({
      data: {
        bookingId,
        notes: nextNotes,
        checklist: nextChecklist,
      },
    });
    if (!result.ok) {
      toast.error(result.error ?? "Could not save the job record");
      return false;
    }
    setNotesDirty(false);
    await queryClient.invalidateQueries({ queryKey: ["partner-job-workspace", bookingId] });
    return true;
  }

  async function toggleChecklist(
    key: "arrived_checked" | "scope_completed" | "left_safe_tidy",
    checked: boolean,
  ) {
    const next = { ...checklist, [key]: checked };
    setChecklist(next);
    const saved = await saveWorkState(next, notes);
    if (!saved) setChecklist(checklist);
  }

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-muted-foreground">This job could not be found.</p>
        <Link to="/partner" className="font-semibold text-primary underline">Back to workspace</Link>
      </div>
    );
  }

  const { booking, events, media, assignment, viewer } = data;

  const serviceName=(booking.services as {name?:string}|null)?.name??"Job";
  const customerName=(booking as {customer_name?:string|null}).customer_name||"Customer";
  const customerPhone=(booking as {customer_phone?:string|null}).customer_phone??null;
  const address=[booking.address_line,booking.town_city,booking.postcode].filter(Boolean).join(", ");
  const date=booking.scheduled_date??booking.preferred_date;
  const window=booking.scheduled_window??booking.preferred_window;
  const active=["assigned","scheduled","on_the_way","arrived","in_progress","rework"].includes(booking.status);
  const [workTab,setWorkTab]=useState<"overview"|"checklist"|"photos"|"notes">("overview");
  const mapsUrl=useMemo(()=>address ? "https://www.google.com/maps/search/?api=1&query="+encodeURIComponent(address) : null,[address]);

  return (
    <div className="partner-work-ref-page">
      <main className="partner-work-ref-main">
        <header className="partner-work-ref-top"><Link to="/partner/jobs" aria-label="Back to jobs"><ArrowLeft/></Link><strong>Job {booking.reference}</strong><span className={"partner-work-ref-status is-"+(booking.status==="in_progress"?"blue":booking.status==="completed"||booking.status==="closed"?"green":"gold")}>{bookingStatusLabel(booking.status)}</span></header>
        <section className="partner-work-ref-intro"><h1>{serviceName}</h1><p>{customerName}</p><span><MapPin/>{booking.town_city||booking.postcode}</span>{assignment?.business_team_members?.display_name&&<small>Assigned to {assignment.business_team_members.display_name}</small>}{viewer.role!=="owner"&&<small>Your role: {viewer.role}</small>}</section>

        <section className="partner-work-ref-tools">
          <a href={customerPhone?("tel:"+customerPhone):"#job-details"} onClick={(e)=>{if(!customerPhone){e.preventDefault();toast.info("Customer phone is not available for this job");}}}><span><Phone/></span><small>Call</small></a>
          <Link to="/partner/messages/$bookingId" params={{bookingId}}><span><MessageCircle/></span><small>Message</small></Link>
          <a href={mapsUrl??"#job-details"} target={mapsUrl?"_blank":undefined} rel={mapsUrl?"noopener noreferrer":undefined} onClick={(e)=>{if(!mapsUrl){e.preventDefault();toast.info("A job address is needed before navigation can open");}}}><span><MapPin/></span><small>Navigate</small></a>
          <button type="button" onClick={()=>setWorkTab("photos")}><span><Camera/></span><small>Photos</small></button>
          <Link to="/partner/business/$module" params={{module:"invoices"}} search={{ bookingId, create: "invoice", customerName }}><span><Receipt/></span><small>Invoice</small></Link>
        </section>

        <nav className="partner-work-ref-tabs">
          {(["overview","checklist","photos","notes"] as const).map(t=><button key={t} className={workTab===t?"active":""} onClick={()=>setWorkTab(t)}>{(t[0]??"").toUpperCase()+t.slice(1)}</button>)}
        </nav>

        {workTab==="overview"&&<section id="job-details" className="partner-work-ref-overview">
          <Info icon={UserRound} label="Customer" value={customerName}/>
          <Info icon={MapPin} label="Address" value={address||"Address available after assignment"}/>
          <Info icon={CalendarDays} label="Date & time" value={date?(ukDate(date)+(window?" · "+window:"")):"To be scheduled"}/>
          <Info icon={Clock3} label="Job details" value={booking.description}/>
          {booking.access_notes&&<Info icon={MapPin} label="Access" value={booking.access_notes}/>}
          {booking.hazards&&<Info icon={CheckCircle2} label="Hazards" value={booking.hazards}/>}
        </section>}

        {workTab==="overview"&&team?.entitled&&team.canManage&&<section className="partner-work-ref-pane"><h2>Team assignment</h2><p>Assign this job to an active team member. Shields blocks an obvious same-window conflict.</p><div className="mt-3 flex flex-wrap gap-2">{team.members.filter((member: { id: string; status: string; display_name: string })=>member.status==="active").map((member: { id: string; status: string; display_name: string })=><Button key={member.id} type="button" variant={assignment?.team_member_id===member.id?"default":"outline"} size="sm" disabled={Boolean(assigningMemberId)||unassigning} onClick={async()=>{if(assigningMemberId||unassigning)return;setAssigningMemberId(member.id);try{const result=await assignPartnerJobToTeamMember({data:{bookingId,teamMemberId:member.id}});if(!result.ok){toast.error(result.error);return;}toast.success("Job assigned to "+member.display_name);await refresh();}catch(error){toast.error(error instanceof Error?error.message:"Could not assign this job");}finally{setAssigningMemberId(null);}}}>{assigningMemberId===member.id?"Assigning…":member.display_name}</Button>)}{assignment&&<Button type="button" variant="outline" size="sm" disabled={Boolean(assigningMemberId)||unassigning} onClick={async()=>{if(assigningMemberId||unassigning)return;setUnassigning(true);try{const result=await unassignPartnerJobFromTeam({data:{bookingId}});if(!result.ok){toast.error(result.error);return;}toast.success("Team assignment removed");await refresh();}catch(error){toast.error(error instanceof Error?error.message:"Could not remove the team assignment");}finally{setUnassigning(false);}}}>{unassigning?"Removing…":"Unassign"}</Button>}</div></section>}

        {workStateError&&<section className="partner-work-ref-pane"><h2>Job record unavailable</h2><p>{workStateError}</p><Button type="button" variant="outline" className="mt-3" onClick={()=>queryClient.invalidateQueries({queryKey:["partner-job-workspace",bookingId]})}>Try again</Button></section>}
        {workTab==="checklist"&&!workStateError&&<section className="partner-work-ref-pane"><h2>Job checklist</h2><p className="mb-3 text-sm text-muted-foreground">Saved to this Shields job so your team sees the same record.</p><label><input type="checkbox" checked={checklist.arrived_checked} onChange={(e)=>void toggleChecklist("arrived_checked",e.target.checked)}/> Arrived and checked the work area</label><label><input type="checkbox" checked={checklist.scope_completed} onChange={(e)=>void toggleChecklist("scope_completed",e.target.checked)}/> Work completed to agreed scope</label><label><input type="checkbox" checked={checklist.left_safe_tidy} onChange={(e)=>void toggleChecklist("left_safe_tidy",e.target.checked)}/> Area left safe and tidy</label><p>Use Photos and Notes to keep evidence with this job.</p></section>}

        {workTab==="photos"&&<section id="completion" className="partner-work-ref-pane"><h2>Work photos</h2><p>Add before and after evidence when completing the job.</p>{media.length>0&&<div className="partner-jobs-ref-photos">{media.slice(0,8).map((item:any)=><img key={item.id} src={item.url} loading="lazy" alt="Job evidence"/>)}</div>}<div className="partner-work-ref-upload-grid"><label>Before photos<Input type="file" accept="image/*" multiple onChange={(e)=>setBeforeFiles(Array.from(e.target.files??[]).slice(0,3))}/></label><label>After photos<Input type="file" accept="image/*" multiple onChange={(e)=>setAfterFiles(Array.from(e.target.files??[]).slice(0,3))}/></label></div></section>}

        {workTab==="notes"&&!workStateError&&<section className="partner-work-ref-pane"><h2>Work notes</h2><p className="mb-3 text-sm text-muted-foreground">Private operational notes saved with this job. Completion notes are still sent separately when you finish the work.</p><Textarea value={notes} onChange={e=>{setNotes(e.target.value);setNotesDirty(true)}} rows={6} maxLength={4000} placeholder="Access details, materials needed, what was found, follow-up notes…"/><Button type="button" className="mt-3 min-h-11" disabled={!notesDirty||busy} onClick={async()=>{setBusy(true);const ok=await saveWorkState(checklist,notes);setBusy(false);if(ok)toast.success("Job notes saved")}}>{busy?"Saving…":"Save notes"}</Button></section>}

        {booking.status==="scheduled"&&<button disabled={busy} onClick={()=>progress("on_the_way")} className="partner-work-ref-complete is-start"><Play/> {busy?"Updating…":"On the way"}</button>}\n        {booking.status==="on_the_way"&&<button disabled={busy} onClick={()=>progress("arrive")} className="partner-work-ref-complete is-start"><MapPin/> {busy?"Updating…":"I’ve arrived"}</button>}\n        {booking.status==="arrived"&&<button disabled={busy} onClick={()=>progress("start")} className="partner-work-ref-complete is-start"><Play/> {busy?"Updating…":"Start Job"}</button>}
        {["in_progress","rework"].includes(booking.status)&&<button disabled={busy} onClick={(e)=>{if(notes.trim().length<10){setWorkTab("notes");toast.info("Add completion notes before marking the job complete");return;}void progress("complete",e as any)}} className="partner-work-ref-complete"><CheckCircle2/> {busy?"Sending…":"Mark as Complete"}</button>}
        {booking.status==="awaiting_customer_approval"&&<div className="partner-work-ref-waiting"><CheckCircle2/><span><strong>Completion submitted</strong><small>Waiting for customer approval.</small></span></div>}
        {!active&&["completed","closed"].includes(booking.status)&&<div className="partner-work-ref-waiting is-complete"><CheckCircle2/><span><strong>Job completed</strong><small>This work has been marked complete.</small></span></div>}

        <details className="partner-work-ref-more"><summary>Schedule, payment &amp; job history</summary>
          {paymentState&&<PartnerPaymentStatusCard bookingStatus={paymentState.bookingStatus} agreedPricePence={paymentState.agreedPricePence} customerApprovedAt={paymentState.customerApprovedAt} payment={paymentState.payment}/>}
          {["assigned","scheduled","on_the_way","arrived","in_progress","awaiting_customer_approval","rework"].includes(booking.status)&&<ScheduleSection bookingId={bookingId} userId={user.id} scheduledDate={booking.scheduled_date} scheduledWindow={booking.scheduled_window}/>}
          <JobLocationMap postcode={booking.postcode} precise/>
          <JobJourney status={booking.status}/>
          <ol>{events.map((e: { id: string; event_type: string; created_at: string })=><li key={e.id}><strong>{e.event_type.replaceAll("_"," ")}</strong> <small>{ukDateTime(e.created_at)}</small></li>)}</ol>
        </details>
      </main>
    </div>
  );
}
function Info({icon:Icon,label,value}:{icon:any;label:string;value:string}){return <div className="partner-work-ref-info"><Icon/><span><small>{label}</small><strong>{value}</strong></span></div>}
